import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileUpload, EvaluationResults, StatusIndicator, LanguageSelector } from './components';
import { useEvaluation } from './hooks';
import { PredictionSegment } from './types';
import WaveSurfer from 'wavesurfer.js';
import { useTranslation } from 'react-i18next';
import './index.css';

// Type pour WaveSurfer avec le plugin Regions
interface WaveSurferWithRegions extends WaveSurfer {
  addRegion: (options: any) => any;
  clearRegions: () => any;
}

// Fonction pour formater la taille du fichier
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Seuil pour considérer un fichier comme volumineux (50 Mo)
const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024;

const App: React.FC = () => {
  const { t } = useTranslation();
  
  // Références pour WaveSurfer
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurferWithRegions | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [waveformLoaded, setWaveformLoaded] = useState<boolean>(false);
  
  // États globaux
  const [file, setFile] = useState<File | null>(null);
  const [showScores, setShowScores] = useState<boolean>(false);
  
  // Hooks personnalisés
  const {
    evaluationId,
    evaluationResult,
    isUploading,
    isEvaluating,
    progress,
    error,
    startEvaluation: startEvaluationHandler,
    resetEvaluation,
  } = useEvaluation({ file });

  // Fonction pour obtenir la couleur en fonction du score
  const getColorForScore = useCallback((score: number): string => {
    if (score > 30) return 'rgba(255, 0, 0, 0.3)'; // Rouge transparent
    if (score >= 15 && score <= 30) return 'rgba(255, 165, 0, 0.3)'; // Orange transparent
    return 'rgba(0, 255, 0, 0.3)'; // Vert transparent
  }, []);

  // Fonction pour obtenir la couleur du texte en fonction du score
  const getTextColorForScore = useCallback((score: number): string => {
    if (score > 30) return '#ff0000'; // Rouge
    if (score >= 15 && score <= 30) return '#ffa500'; // Orange
    return '#00ff00'; // Vert
  }, []);

  // Initialiser WaveSurfer avec optimisations pour les gros fichiers
  useEffect(() => {
    if (waveformRef.current && file) {
      const RegionsPlugin = require('wavesurfer.js/dist/plugin/wavesurfer.regions.min.js');
      
      // Déterminer si le fichier est volumineux
      const isLargeFile = file.size > LARGE_FILE_THRESHOLD;
      
      // Configuration adaptative en fonction de la taille du fichier
      const waveSurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: 'rgb(29, 186, 165)',
        progressColor: 'rgb(246, 136, 7)',
        cursorColor: 'rgb(72, 78, 82)',
        // Paramètres optimisés pour les gros fichiers
        barWidth: isLargeFile ? 1 : 2,
        barRadius: isLargeFile ? 1 : 3,
        cursorWidth: 1,
        height: 100,
        barGap: isLargeFile ? 1 : 2,
        interact: true,
        normalize: true,     // Normaliser pour éviter les pics de volume
        partialRender: true,  // Rendre partiellement pendant le chargement
        // Utiliser MediaElement backend pour les fichiers volumineux
        // Cela permet une meilleure compatibilité et réduit la charge mémoire
        backend: isLargeFile ? 'MediaElement' : 'WebAudio',
        plugins: [RegionsPlugin.create()],
      });

      waveSurferRef.current = waveSurfer as unknown as WaveSurferWithRegions;

      // Mettre à jour l'état isPlaying
      waveSurfer.on('play', () => setIsPlaying(true));
      waveSurfer.on('pause', () => setIsPlaying(false));
      waveSurfer.on('finish', () => setIsPlaying(false));
      waveSurfer.on('ready', () => {
        setWaveformLoaded(true);
      });
      waveSurfer.on('seek', () => {
        if (!waveSurfer.isPlaying()) {
          waveSurfer.play();
          setIsPlaying(true);
        }
      });

      const objectUrl = URL.createObjectURL(file);
      waveSurfer.load(objectUrl);

      return () => {
        waveSurfer.destroy();
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [file]);

  // Ajouter les régions pour les segments de prédiction
  useEffect(() => {
    if (waveSurferRef.current && evaluationResult?.prediction_list && evaluationResult.prediction_list.length > 0 && showScores) {
      const waveSurfer = waveSurferRef.current;
      
      // Effacer les régions existantes
      waveSurfer.clearRegions();

      // Ajouter une région pour chaque segment
      evaluationResult.prediction_list.forEach((segment: PredictionSegment, index: number) => {
        const startTime = segment.start;
        const endTime = segment.end;
        const score = segment.prediction;
        const color = getColorForScore(score);

        const region = waveSurfer.addRegion({
          start: startTime,
          end: endTime,
          color: color,
          drag: false,
          resize: false,
          content: `Score: ${score.toFixed(2)}`,
          attributes: {
            'data-score': score.toFixed(2),
            'data-segment': index + 1
          }
        });

        // Rendre la région cliquable pour jouer le segment
        region.on('click', () => {
          if (waveSurferRef.current) {
            waveSurferRef.current.play(startTime, endTime);
            setIsPlaying(true);
          }
        });
      });
    }
  }, [evaluationResult, showScores, getColorForScore]);

  // Jouer un segment
  const playSegment = useCallback((start: number, end: number) => {
    if (waveSurferRef.current) {
      waveSurferRef.current.play(start, end);
      setIsPlaying(true);
    }
  }, []);

  // Basculer lecture/pause
  const togglePlayPause = useCallback(() => {
    if (waveSurferRef.current) {
      if (waveSurferRef.current.isPlaying()) {
        waveSurferRef.current.pause();
        setIsPlaying(false);
      } else {
        waveSurferRef.current.play();
        setIsPlaying(true);
      }
    }
  }, []);

  // Réinitialiser tout
  const handleReset = useCallback(() => {
    setFile(null);
    resetEvaluation();
    setShowScores(false);
    setIsPlaying(false);
    setWaveformLoaded(false);
    if (waveSurferRef.current) {
      waveSurferRef.current.destroy();
      waveSurferRef.current = null;
    }
    if (waveformRef.current) {
      waveformRef.current.innerHTML = '';
    }
  }, [resetEvaluation]);

  // Démarrer l'évaluation
  const handleStartEvaluation = useCallback(async () => {
    await startEvaluationHandler();
  }, [startEvaluationHandler]);

  // Mettre à jour showScores quand evaluationResult change
  useEffect(() => {
    if (evaluationResult && evaluationResult.prediction !== null && evaluationResult.prediction_list) {
      setShowScores(true);
    }
  }, [evaluationResult]);

  return (
    <div className="container">
      {/* En-tête */}
      <div className="header">
        <h1>{t('app.title')}</h1>
        <p className="subtitle">
          {t('app.subtitle')}
          <span className="highlight-blue"> {t('app.highlight')}</span>
          <span className="info-tooltip">
            ?
            <span className="tooltip-text">
              <strong>{t('app.tooltip.title')}</strong> {t('app.tooltip.description')}<br />
              {t('app.tooltip.technology')}<br />
              {t('app.tooltip.demoShows')}<br />
              • {t('app.tooltip.globalScore')}<br />
              • {t('app.tooltip.segmentedAnalysis')}
            </span>
          </span>
        </p>
      </div>

      {/* Bloc de sélection de fichier - affiché initialement */}
      {!file && !evaluationResult && (
        <div className="card">
          <h2>{t('fileUpload.title')}</h2>
          <p>{t('fileUpload.description')}</p>
          
          {/* Composant FileUpload */}
          <FileUpload 
            disabled={isUploading || isEvaluating} 
            onFileSelect={setFile}
            fileInfo={null}
            formatFileSize={formatFileSize}
          />
        </div>
      )}

      {/* Bloc Démarrez l'évaluation - affiché quand un fichier est sélectionné */}
      {file && !evaluationId && !isEvaluating && !evaluationResult && (
        <div className="card">
          <h2>{t('evaluation.startTitle')}</h2>
          
          {/* Bouton Démarrer l'évaluation avec surbrillance animée */}
          <button
            className="button glow-button"
            onClick={handleStartEvaluation}
            disabled={isUploading || isEvaluating}
          >
            {t('evaluation.startButton')}
          </button>
        </div>
      )}

      {/* Barre de progression pour l'upload et l'évaluation */}
      {(isUploading || isEvaluating) && (
        <div className="card">
          <StatusIndicator
            isUploading={isUploading}
            isEvaluating={isEvaluating}
            progress={progress}
            evaluationId={evaluationId}
            error={error}
          />
        </div>
      )}

      {/* Carte de visualisation audio */}
      {file && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2>{t('evaluation.waveformTitle')}</h2>
          </div>
          
          {/* Indicateur de chargement de l'onde sonore */}
          {!waveformLoaded && (
            <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                ⏳ {t('evaluation.uploadProgress')}
              </p>
            </div>
          )}
          
          {/* Message pour les fichiers volumineux */}
          {file.size > LARGE_FILE_THRESHOLD && (
            <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff8e1', borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                ⚠️ {t('evaluation.largeFileWarning', { size: formatFileSize(file.size) })}
              </p>
            </div>
          )}
          
          {/* Afficher les infos du fichier */}
          {file && (
            <div className="file-info" style={{ marginBottom: '10px' }}>
              <span><strong>{t('fileUpload.fileInfo', { name: file.name })}</strong></span>
              <span style={{ marginLeft: '15px' }}><strong>{t('fileUpload.fileSize', { size: formatFileSize(file.size) })}</strong></span>
              <span style={{ marginLeft: '15px' }}><strong>{t('fileUpload.fileType', { type: file.type })}</strong></span>
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <button
              className="button"
              onClick={togglePlayPause}
              disabled={!waveSurferRef.current || !waveSurferRef.current.isReady}
            >
              {isPlaying ? t('evaluation.pauseButton') : t('evaluation.playButton')}
            </button>
            <span style={{ fontSize: '14px', color: 'rgb(72, 78, 82)' }}>
              {waveSurferRef.current ? `${waveSurferRef.current.getCurrentTime().toFixed(1)}s / ${waveSurferRef.current.getDuration().toFixed(1)}s` : '0.0s / 0.0s'}
            </span>
          </div>
          
          {/* Conteneur de l'onde sonore */}
          <div className="waveform-container" ref={waveformRef} style={{ minHeight: '100px' }}></div>
          
          {showScores && evaluationResult?.prediction_list && evaluationResult.prediction_list.length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              <p>🟢 {t('scores.good')}</p>
              <p>🟡 {t('scores.medium')}</p>
              <p>🔴 {t('scores.bad')}</p>
            </div>
          )}
        </div>
      )}

      {/* Score global coloré */}
      {evaluationResult && evaluationResult.prediction !== null && (
        <div className="card" style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div className="score-global">
            {t('evaluation.globalScoreTitle')} :&nbsp;
            <span style={{ color: getTextColorForScore(evaluationResult.prediction) }}>
              {evaluationResult.prediction.toFixed(2)}
            </span>
            <br />
            <span style={{ fontSize: '16px', fontWeight: 'normal' }}>
              ({t('evaluation.globalScoreSubtitle')})
            </span>
          </div>
        </div>
      )}

      {/* Composant EvaluationResults */}
      {evaluationResult && evaluationResult.prediction !== null && (
        <EvaluationResults
          evaluationResult={evaluationResult}
          onSegmentClick={playSegment}
        />
      )}

      {/* Message si les résultats ne sont pas disponibles */}
      {evaluationResult && evaluationResult.prediction === null && evaluationResult.status === 'finished' && (
        <div className="card" style={{ backgroundColor: '#fff8e1' }}>
          <h3>⚠️ {t('evaluation.resultsUnavailable')}</h3>
          <p>
            {t('evaluation.resultsUnavailableDescription', { status: evaluationResult.status })}
          </p>
        </div>
      )}

      {/* Afficher l'erreur si présente */}
      {error && !isUploading && !isEvaluating && (
        <div className="card" style={{ backgroundColor: '#ffebee' }}>
          <div className="error">{error}</div>
        </div>
      )}

      {/* Bloc Nouveau fichier - positionné juste avant le pied de page, apparaît après évaluation */}
      {evaluationResult && evaluationResult.prediction !== null && (
        <div className="card" style={{ textAlign: 'center' }}>
          <button className="button glow-button" onClick={handleReset}>
            {t('evaluation.newFileButton')}
          </button>
        </div>
      )}

      {/* Pied de page */}
      <div className="card" style={{ marginTop: '20px', textAlign: 'center', position: 'relative' }}>
        <p style={{ color: 'rgb(72, 78, 82)', fontSize: '14px' }}>
          {t('footer.text')}<br />
          {t('footer.poweredBy')}
          <a href="https://authot.com" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px' }}>
            Authôt
          </a>
        </p>
        {/* Sélecteur de langue - positionné en bas à droite */}
        <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
          <LanguageSelector />
        </div>
      </div>
    </div>
  );
};

export default App;
